import { z } from 'zod';
import { IsoDateTimeSchema, RialStringSchema, UuidSchema } from './common.js';

/**
 * Migration Tools (docs/migration-tools/design.md, ADR-032): importing patients from a
 * spreadsheet and exporting all of a clinic's data. The browser only turns the file into raw
 * cell text; every normalization, validation and duplicate decision is made by the API.
 */

// ---------------------------------------------------------------- patient import

export const PATIENT_IMPORT_MAX_ROWS = 5000;
/** Longest raw cell the API accepts (the medical notes column is the longest target, 2000). */
export const PATIENT_IMPORT_MAX_CELL = 2000;

export const PATIENT_IMPORT_FIELDS = [
  'externalRef',
  'fileNumber',
  'firstName',
  'lastName',
  'fullName',
  'phone',
  'nationalCode',
  'gender',
  'birthDate',
  'referralSource',
  'openingBalance',
  'allergies',
  'conditions',
  'medications',
  'medicalNotes',
] as const;
export type PatientImportField = (typeof PATIENT_IMPORT_FIELDS)[number];

/** Columns that write the medical profile: the importer needs `medical.write` (ADR-026). */
export const PATIENT_IMPORT_MEDICAL_FIELDS = [
  'allergies',
  'conditions',
  'medications',
  'medicalNotes',
] as const;

/**
 * The template's column titles and the titles that suggest a mapping. Matching ignores spaces,
 * ZWNJ, Arabic letter forms and case. The first title is the template's.
 */
export const PATIENT_IMPORT_HEADERS: Readonly<Record<PatientImportField, readonly string[]>> = {
  externalRef: ['شناسه‌ی قبلی', 'شناسه قبلی', 'external ref', 'id'],
  // The clinic's file number (BR-73): kept on the client and shown, unlike the previous id.
  fileNumber: ['شماره‌ی پرونده', 'شماره پرونده', 'کد پرونده', 'file number', 'file no'],
  firstName: ['نام', 'اسم', 'first name', 'firstname'],
  lastName: ['نام خانوادگی', 'فامیل', 'فامیلی', 'last name', 'lastname', 'surname'],
  fullName: ['نام کامل', 'نام و نام خانوادگی', 'نام کلاینت', 'نام بیمار', 'نام مراجع', 'full name', 'name'],
  phone: [
    'موبایل',
    'تلفن همراه',
    'شماره موبایل',
    'شماره همراه',
    'همراه',
    'تلفن',
    'شماره تماس',
    'mobile',
    'phone',
  ],
  nationalCode: ['کد ملی', 'کدملی', 'شماره ملی', 'national code', 'national id'],
  gender: ['جنسیت', 'جنس', 'gender', 'sex'],
  birthDate: ['تاریخ تولد', 'تولد', 'birth date', 'birthdate', 'date of birth'],
  referralSource: ['منبع آشنایی', 'نحوه آشنایی', 'آشنایی', 'referral source', 'source'],
  openingBalance: ['مانده‌ی بدهی', 'مانده بدهی', 'بدهی', 'مانده', 'بدهکاری', 'balance', 'debt'],
  allergies: ['حساسیت‌ها', 'حساسیت', 'آلرژی', 'allergies', 'allergy'],
  conditions: ['بیماری‌های زمینه‌ای', 'بیماری زمینه‌ای', 'بیماری‌ها', 'سابقه بیماری', 'conditions'],
  medications: ['داروهای مصرفی', 'داروها', 'دارو', 'medications'],
  medicalNotes: ['یادداشت پزشکی', 'سابقه پزشکی', 'سابقه', 'توضیحات پزشکی', 'medical notes', 'notes'],
};

export const BALANCE_UNITS = ['toman', 'rial'] as const;
export type BalanceUnit = (typeof BALANCE_UNITS)[number];

const rawCell = z
  .string()
  .max(PATIENT_IMPORT_MAX_CELL, { message: `هر خانه حداکثر ${PATIENT_IMPORT_MAX_CELL} نویسه.` });

/** One spreadsheet row, already mapped to target fields by the browser. Values are raw text. */
export const PatientImportRowSchema = z
  .object({
    /** The row number in the file, for messages. */
    rowNumber: z.number().int().min(1).max(1_048_576),
    ...(Object.fromEntries(PATIENT_IMPORT_FIELDS.map((f) => [f, rawCell.optional()])) as Record<
      PatientImportField,
      z.ZodOptional<typeof rawCell>
    >),
  })
  .strict();
export type PatientImportRow = z.infer<typeof PatientImportRowSchema>;

export const PatientImportRequestSchema = z
  .object({
    fileName: z.string().trim().min(1).max(200),
    balanceUnit: z.enum(BALANCE_UNITS),
    rows: z
      .array(PatientImportRowSchema)
      .min(1, { message: 'فایل ردیفی ندارد.' })
      .max(PATIENT_IMPORT_MAX_ROWS, { message: `حداکثر ${PATIENT_IMPORT_MAX_ROWS} ردیف در هر ورود.` }),
  })
  .refine((v) => new Set(v.rows.map((r) => r.rowNumber)).size === v.rows.length, {
    message: 'شماره‌ی ردیف تکراری است.',
    path: ['rows'],
  });
export type PatientImportRequest = z.input<typeof PatientImportRequestSchema>;

export const PATIENT_IMPORT_OUTCOMES = ['create', 'duplicate', 'invalid'] as const;
export type PatientImportOutcome = (typeof PATIENT_IMPORT_OUTCOMES)[number];

/** Why a value was rejected. The web turns each code into a Persian message. */
export const PATIENT_IMPORT_ERROR_CODES = [
  'required',
  'too_long',
  'invalid_phone',
  'invalid_national_code',
  'invalid_gender',
  'invalid_birth_date',
  'invalid_referral_source',
  'invalid_amount',
  'negative_amount',
] as const;
export type PatientImportErrorCode = (typeof PATIENT_IMPORT_ERROR_CODES)[number];

export const PATIENT_IMPORT_WARNING_CODES = [
  // another patient (an existing one or an earlier row) has this phone under another name
  'shared_phone',
  // Excel dropped the leading zeros of the national code; they were put back
  'national_code_padded',
  // a condition that is not in the fixed list went to "other conditions"
  'unknown_condition',
  // the row is a duplicate, so its balance is not imported
  'balance_not_imported',
] as const;
export type PatientImportWarningCode = (typeof PATIENT_IMPORT_WARNING_CODES)[number];

export const PATIENT_DUPLICATE_KEYS = ['external_ref', 'file_number', 'national_code', 'phone_name'] as const;
export type PatientDuplicateKey = (typeof PATIENT_DUPLICATE_KEYS)[number];

export const PatientImportIssueSchema = z.object({
  field: z.enum(PATIENT_IMPORT_FIELDS),
  code: z.string(),
});

export const PatientImportRowResultSchema = z.object({
  rowNumber: z.number().int(),
  outcome: z.enum(PATIENT_IMPORT_OUTCOMES),
  /** Display name: the normalized name of the row, or of the patient it created or matched. */
  name: z.string().nullable(),
  phone: z.string().nullable(),
  openingBalanceRial: RialStringSchema.nullable(),
  errors: z.array(PatientImportIssueSchema),
  warnings: z.array(PatientImportIssueSchema),
  /** For a duplicate: the existing patient, or the earlier row of the same file. */
  duplicateOf: z
    .object({
      by: z.enum(PATIENT_DUPLICATE_KEYS),
      patientId: UuidSchema.nullable(),
      rowNumber: z.number().int().nullable(),
    })
    .nullable(),
  /** The patient this row created (after the import) or matched. */
  patientId: UuidSchema.nullable(),
});
export type PatientImportRowResult = z.infer<typeof PatientImportRowResultSchema>;

export const PatientImportTotalsSchema = z.object({
  rows: z.number().int(),
  create: z.number().int(),
  duplicate: z.number().int(),
  invalid: z.number().int(),
  withWarnings: z.number().int(),
  openingBalanceRows: z.number().int(),
  openingBalanceTotalRial: RialStringSchema,
});
export type PatientImportTotals = z.infer<typeof PatientImportTotalsSchema>;

export const PatientImportPreviewSchema = z.object({
  totals: PatientImportTotalsSchema,
  rows: z.array(PatientImportRowResultSchema),
});
export type PatientImportPreview = z.infer<typeof PatientImportPreviewSchema>;

export const PatientImportSummarySchema = z.object({
  id: UuidSchema,
  fileName: z.string(),
  rowCount: z.number().int(),
  createdCount: z.number().int(),
  duplicateCount: z.number().int(),
  invalidCount: z.number().int(),
  openingBalanceCount: z.number().int(),
  openingBalanceTotalRial: RialStringSchema,
  withMedical: z.boolean(),
  createdAt: IsoDateTimeSchema,
  createdByName: z.string().nullable(),
});
export type PatientImportSummary = z.infer<typeof PatientImportSummarySchema>;

export const PatientImportListResponseSchema = z.object({ items: z.array(PatientImportSummarySchema) });
export type PatientImportListResponse = z.infer<typeof PatientImportListResponseSchema>;

export const PatientImportDetailSchema = z.object({
  import: PatientImportSummarySchema,
  rows: z.array(PatientImportRowResultSchema),
});
export type PatientImportDetail = z.infer<typeof PatientImportDetailSchema>;

// ---------------------------------------------------------------- data export

export const DATA_EXPORT_STATUSES = ['queued', 'running', 'ready', 'failed', 'expired'] as const;
export type DataExportStatus = (typeof DATA_EXPORT_STATUSES)[number];

/** Hours a ready export can be downloaded before its file is deleted. */
export const DATA_EXPORT_TTL_HOURS = 24;

export const DATA_EXPORT_EXCLUSION_REASONS = ['medical_read'] as const;

export const DataExportFileSchema = z.object({ name: z.string(), rows: z.number().int() });

export const DataExportSchema = z.object({
  id: UuidSchema,
  status: z.enum(DATA_EXPORT_STATUSES),
  errorCode: z.string().nullable(),
  fileName: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  sha256: z.string().nullable(),
  files: z.array(DataExportFileSchema),
  excluded: z.array(z.object({ name: z.string(), reason: z.enum(DATA_EXPORT_EXCLUSION_REASONS) })),
  requestedByName: z.string().nullable(),
  /** Only the member who requested an export can download it. */
  mine: z.boolean(),
  createdAt: IsoDateTimeSchema,
  finishedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
});
export type DataExport = z.infer<typeof DataExportSchema>;

export const DataExportListResponseSchema = z.object({ items: z.array(DataExportSchema) });
export type DataExportListResponse = z.infer<typeof DataExportListResponseSchema>;
